// 1-Click School Showcase Video Wizard
// =====================================
// School Admin: literal 1-click — profile data auto-used
// Platform Admin: quick form to enter school data (demo/outreach mode)
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_COLORS = {
  welcome_tour: 'from-orange-400 to-amber-500',
  celebration: 'from-pink-400 to-rose-500',
  achievement: 'from-yellow-400 to-orange-500',
  principal_vision: 'from-blue-500 to-indigo-600',
};

export default function SchoolShowcaseWizard({ token, user, language = 'hi' }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null); // template_id being submitted
  const [msg, setMsg] = useState(null);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [customTagline, setCustomTagline] = useState('');
  const [topReco, setTopReco] = useState(null);  // {template_id, lift_pct_vs_avg, ...}

  // Platform admin override fields
  const isPlatformAdmin = user?.role === 'platform_admin';
  const [schoolOverride, setSchoolOverride] = useState({
    name: '', city: '', state: '', board: 'CBSE', medium: 'English Medium', established_year: '',
  });

  useEffect(() => {
    fetch(`${API_URL}/api/veo/showcase/templates?language=${language}`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setTemplates(d.templates || []); })
      .catch((e) => console.warn('templates load:', e))
      .finally(() => setLoading(false));

    // Fetch top-performer recommendation (decorate matching card with 🔥 badge)
    fetch(`${API_URL}/api/veo/analytics/recommendations?range=30d`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.success) setTopReco(d.top_template); })
      .catch(() => {});
  }, [language, token]);

  const submit = async (template_id) => {
    if (isPlatformAdmin && !schoolOverride.name.trim()) {
      setMsg({ type: 'error', text: language === 'hi' ? 'Platform Admin मोड में कम से कम School Name भरें' : 'Platform Admin mode needs a school name' });
      return;
    }
    setSubmitting(template_id); setMsg(null);
    try {
      const body = {
        template_id,
        aspect_ratio: aspectRatio,
        resolution: '720p',
      };
      if (customTagline.trim()) body.custom_tagline = customTagline.trim();
      if (isPlatformAdmin) {
        body.school_override = {
          ...schoolOverride,
          established_year: schoolOverride.established_year ? Number(schoolOverride.established_year) : null,
        };
      }
      const r = await fetch(`${API_URL}/api/veo/showcase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setMsg({ type: 'success', text: (language === 'hi' ? '✅ Job submitted — ' : '✅ Queued — ') + d.title + ' (Job: ' + d.job_id.slice(-8) + ')' });
      } else {
        setMsg({ type: 'error', text: d.detail || 'Submit failed' });
      }
    } catch (e) { setMsg({ type: 'error', text: 'Error: ' + e.message }); }
    setSubmitting(null);
  };

  return (
    <div className="space-y-4" data-testid="school-showcase-wizard">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-5 text-white">
        <h3 className="text-xl font-black" style={{ fontFamily: 'Outfit' }}>
          ⚡ {language === 'hi' ? '1-Click School Showcase Video' : '1-Click School Showcase Video'}
        </h3>
        <p className="text-sm text-white/90 mt-1">
          {language === 'hi'
            ? 'एक click में personalized marketing video — WhatsApp, Instagram, YouTube पर share-ready'
            : 'Generate a personalized marketing video in one click — ready to share on WhatsApp, Instagram, YouTube.'}
        </p>
      </div>

      {/* Platform Admin override form */}
      {isPlatformAdmin && (
        <div className="bg-white rounded-xl p-4 border" data-testid="showcase-platform-override">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            {language === 'hi' ? 'Platform Admin Mode — Demo School की details भरें:' : 'Platform Admin Mode — enter school details for demo:'}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <input placeholder="School Name *" value={schoolOverride.name} onChange={(e) => setSchoolOverride({ ...schoolOverride, name: e.target.value })} className="px-2 py-1.5 border rounded text-sm" data-testid="override-school-name" />
            <input placeholder="City" value={schoolOverride.city} onChange={(e) => setSchoolOverride({ ...schoolOverride, city: e.target.value })} className="px-2 py-1.5 border rounded text-sm" data-testid="override-city" />
            <input placeholder="State" value={schoolOverride.state} onChange={(e) => setSchoolOverride({ ...schoolOverride, state: e.target.value })} className="px-2 py-1.5 border rounded text-sm" data-testid="override-state" />
            <select value={schoolOverride.board} onChange={(e) => setSchoolOverride({ ...schoolOverride, board: e.target.value })} className="px-2 py-1.5 border rounded text-sm" data-testid="override-board">
              <option>CBSE</option><option>ICSE</option><option>MP Board</option><option>State Board</option><option>IB</option>
            </select>
            <select value={schoolOverride.medium} onChange={(e) => setSchoolOverride({ ...schoolOverride, medium: e.target.value })} className="px-2 py-1.5 border rounded text-sm" data-testid="override-medium">
              <option>English Medium</option><option>Hindi Medium</option><option>Bilingual</option>
            </select>
            <input type="number" placeholder="Est. Year" value={schoolOverride.established_year} onChange={(e) => setSchoolOverride({ ...schoolOverride, established_year: e.target.value })} className="px-2 py-1.5 border rounded text-sm" data-testid="override-year" />
          </div>
        </div>
      )}

      {/* Common controls */}
      <div className="bg-white rounded-xl p-4 border flex flex-wrap gap-3 items-end">
        <label className="text-xs">
          <span className="text-gray-500 block mb-1">{language === 'hi' ? 'Aspect Ratio' : 'Aspect Ratio'}</span>
          <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className="px-2 py-1.5 border rounded text-sm" data-testid="showcase-aspect">
            <option value="16:9">16:9 (YouTube)</option>
            <option value="9:16">9:16 (Reels/Shorts)</option>
          </select>
        </label>
        <label className="text-xs flex-1 min-w-[200px]">
          <span className="text-gray-500 block mb-1">{language === 'hi' ? 'Tagline (optional — mood cue)' : 'Tagline (optional — mood cue)'}</span>
          <input type="text" value={customTagline} onChange={(e) => setCustomTagline(e.target.value)} maxLength={200} placeholder={language === 'hi' ? 'जैसे: "Excellence since 1985"' : 'e.g. "Excellence since 1985"'} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="showcase-tagline" />
        </label>
      </div>

      {msg && (
        <div className={`text-sm px-4 py-2 rounded-lg ${msg.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="showcase-msg">
          {msg.text}
        </div>
      )}

      {/* Template Cards — 1-Click */}
      {loading ? (
        <p className="text-sm text-gray-500">Loading templates…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((t) => {
            const isSubmitting = submitting === t.id;
            const grad = TEMPLATE_COLORS[t.id] || 'from-slate-500 to-slate-700';
            const isTopPerformer = topReco?.template_id === t.id && (topReco?.lift_pct_vs_avg || 0) > 0;
            return (
              <div key={t.id} className={`relative rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br ${grad} text-white p-5 hover:scale-[1.02] transition-transform`} data-testid={`showcase-card-${t.id}`}>
                {isTopPerformer && (
                  <div className="absolute top-2 right-2 bg-yellow-300 text-yellow-900 text-[10px] font-black px-2 py-0.5 rounded-full shadow-lg" data-testid={`top-performer-badge-${t.id}`}>
                    🔥 +{Math.round(topReco.lift_pct_vs_avg)}% engagement
                  </div>
                )}
                <div className="text-3xl mb-2">{t.title.match(/^\S+/)?.[0] || '🎬'}</div>
                <h4 className="font-black text-lg leading-tight" style={{ fontFamily: 'Outfit' }}>
                  {t.title.replace(/^\S+\s*/, '')}
                </h4>
                <p className="text-sm text-white/90 mt-1 min-h-[40px]">{t.description}</p>
                <p className="text-[10px] text-white/70 mt-2 uppercase tracking-wider font-semibold">
                  Best for: {t.best_for}
                </p>
                <button
                  onClick={() => submit(t.id)}
                  disabled={!!submitting}
                  className="mt-4 w-full py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl font-bold text-sm disabled:opacity-50"
                  data-testid={`showcase-generate-${t.id}`}
                >
                  {isSubmitting ? (language === 'hi' ? 'Submitting…' : 'Submitting…') : (language === 'hi' ? '⚡ 1-Click Generate' : '⚡ 1-Click Generate')}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-gray-500 italic">
        {language === 'hi'
          ? '⏱ Video ~50-90 sec में ready होगा। "🎬 AI Videos" tab में preview + download करें।'
          : '⏱ Video ready in ~50-90 sec. Preview + download from the "🎬 AI Videos" tab.'}
      </p>
    </div>
  );
}
